<template>
	<div class="range-notched-slider" :data-disabled="disabled ? '' : undefined">
		<label v-if="label" class="label">{{ label }}</label>

		<div class="rail-wrapper">
			<Slider.Root
				:model-value="localPercents"
				@update:model-value="handleSliderChange"
				@value-change-end="handleSliderEnd"
				@focus-change="({ focusedIndex }) => (focusedThumbIndex = focusedIndex)"
				:min="0"
				:max="100"
				:step="0.1"
				thumb-alignment="center"
				:disabled="disabled"
				class="rail"
				@click="handleRailClick"
			>
				<Slider.MarkerGroup>
					<Slider.Marker v-for="i in notchValues.length" :key="i" :value="notchPercent(i - 1)" />
				</Slider.MarkerGroup>

				<Slider.Control>
					<Slider.Track><Slider.Range /></Slider.Track>
					<Slider.Thumb :index="0"><Slider.HiddenInput /></Slider.Thumb>
					<Slider.Thumb :index="1"><Slider.HiddenInput /></Slider.Thumb>
				</Slider.Control>
			</Slider.Root>

			<div class="notch-labels">
				<div class="notch-labels-inner">
					<span
						v-for="(text, i) in notchLabels"
						:key="i"
						class="notch-label"
						:class="[
							{ active: notchPercent(i) >= localPercents[0] && notchPercent(i) <= localPercents[1] },
							i === 0 ? 'align-start' : i === notchLabels.length - 1 ? 'align-end' : 'align-center',
						]"
						:style="{ left: `${notchPercent(i)}%` }"
						>{{ text }}</span
					>
				</div>
			</div>
		</div>

		<div v-if="isEditing" class="inputs">
			<div class="input-wrapper">
				<label class="sr-only">{{ $t("filters.range-min-placeholder") }}</label>
				<input
					ref="minInputRef"
					type="text"
					:inputmode="precision > 0 ? 'decimal' : 'numeric'"
					:placeholder="$t('filters.range-min-placeholder')"
					:disabled="disabled"
					class="input"
					@input="e => handleInput(e, 'min')"
					@blur="() => handleBlur('min')"
					@keydown="handleKeydown"
				/>
				<span v-if="unitSuffix" class="unit">{{ unitSuffix }}</span>
			</div>

			<span aria-hidden="true" class="dash">–</span>

			<div class="input-wrapper">
				<label class="sr-only">{{ $t("filters.range-max-placeholder") }}</label>
				<input
					ref="maxInputRef"
					type="text"
					:inputmode="precision > 0 ? 'decimal' : 'numeric'"
					:placeholder="$t('filters.range-max-placeholder')"
					:disabled="disabled"
					class="input"
					@input="e => handleInput(e, 'max')"
					@blur="() => handleBlur('max')"
					@keydown="handleKeydown"
				/>
				<span v-if="unitSuffix" class="unit">{{ unitSuffix }}</span>
			</div>

			<button
				type="button"
				class="close-edit"
				:aria-label="$t('filters.close')"
				:title="$t('filters.close')"
				@click="closeEdit"
			>
				×
			</button>
		</div>

		<div v-else class="summary-row">
			<span class="summary-text" :class="{ placeholder: isDefaultRange }">
				{{ isDefaultRange ? $t("filters.range-all") : summaryText }}
			</span>
			<button type="button" class="link" @click="openEdit">{{ $t("filters.range-edit") }}</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, nextTick } from "vue"
import { useI18n } from "vue-i18n"
import { Slider } from "@ark-ui/vue"
import { formatCompactNumber, formatQuantity } from "@/utils/format"

const { t, locale } = useI18n()

const props = withDefaults(
	defineProps<{
		modelValue: [number, number]
		min?: number
		max: number
		thresholds: [number, number, number]
		openEndedAnchor?: number
		unitPrefix?: string
		unitSuffix?: string
		precision?: number
		label?: string
		disabled?: boolean
	}>(),
	{
		min: 0,
		unitPrefix: "",
		unitSuffix: "",
		precision: 0,
		label: "",
		disabled: false,
	}
)

const emit = defineEmits<{
	(e: "update:modelValue", v: [number, number]): void
	(e: "change", v: { from: [number, number]; to: [number, number] }): void
}>()

const committedValue = ref<[number, number]>([...props.modelValue])
const isDragging = ref(false)
const dragChangeCount = ref(0)
const movedThumbs = ref<[boolean, boolean]>([false, false])
const focusedThumbIndex = ref(-1)
const suppressNextClick = ref(false)
const isEditingMin = ref(false)
const isEditingMax = ref(false)
const isEditing = ref(false)
const minInputRef = ref<HTMLInputElement>()
const maxInputRef = ref<HTMLInputElement>()

const openEndedAnchor = computed(() => props.openEndedAnchor ?? props.thresholds[props.thresholds.length - 1] * 4)

// The last notch is a real, reachable anchor (e.g. 1M) that the scale graduates smoothly toward —
// true unbounded max only kicks in once the thumb reaches the very end of the track (percent >= 100).
const notchValues = computed(() => [props.min, ...props.thresholds, openEndedAnchor.value])

function parseNumber(value: string): number {
	const decimal =
		new Intl.NumberFormat(locale.value).formatToParts(1.1).find(part => part.type === "decimal")?.value ?? "."
	return parseFloat(value.replace(decimal, ".").replace(/[^\d.-]/g, "")) || 0
}
const formatNumber = (val: number): string =>
	formatQuantity(val, {
		minimumFractionDigits: props.precision,
		maximumFractionDigits: props.precision,
	})
const formatValue = (val: number): string => `${props.unitPrefix}${formatNumber(val)}${props.unitSuffix}`

const notchLabels = computed(() => {
	const values = notchValues.value
	return values.map((v, i) => (i === values.length - 1 ? `${formatCompactNumber(v)}+` : formatCompactNumber(v)))
})

function valueToPercent(val: number): number {
	const notches = notchValues.value
	if (val >= notches[notches.length - 1]) return 100
	const segmentCount = notches.length - 1
	for (let i = 0; i < segmentCount; i++) {
		const lo = notches[i]
		const hi = notches[i + 1]
		if (val <= hi || i === segmentCount - 1) {
			const frac = hi === lo ? 0 : (Math.min(Math.max(val, lo), hi) - lo) / (hi - lo)
			return ((i + frac) / segmentCount) * 100
		}
	}
	return 100
}

function niceStep(range: number): number {
	if (range <= 0) return 1
	// ~12 steps per segment, rounded to a human-friendly increment (1 / 2 / 2.5 / 5 / 10 × a power of ten)
	const rough = range / 12
	const magnitude = 10 ** Math.floor(Math.log10(rough))
	const normalized = rough / magnitude
	const niceNormalized =
		normalized < 1.5 ? 1 : normalized < 2.25 ? 2 : normalized < 3.5 ? 2.5 : normalized < 7.5 ? 5 : 10
	const step = niceNormalized * magnitude
	// integer-only fields (precision 0) shouldn't land on a fractional step like 2.5
	return props.precision > 0 ? step : Math.max(1, Math.round(step))
}

function rawValue(percent: number): number {
	if (percent >= 100) return props.max
	const notches = notchValues.value
	const segmentCount = notches.length - 1
	const scaled = Math.min(Math.max(percent, 0), 100) / (100 / segmentCount)
	const segmentIndex = Math.min(Math.floor(scaled), segmentCount - 1)
	const frac = scaled - segmentIndex
	const lo = notches[segmentIndex]
	const hi = notches[segmentIndex + 1]
	return lo + frac * (hi - lo)
}

function percentToValue(percent: number): number {
	if (percent >= 100) return props.max
	const notches = notchValues.value
	const segmentCount = notches.length - 1
	for (let i = 0; i <= segmentCount; i++) {
		if (Math.abs(notchPercent(i) - percent) < 1e-6) return notches[i]
	}
	const scaled = Math.min(Math.max(percent, 0), 100) / (100 / segmentCount)
	const segmentIndex = Math.min(Math.floor(scaled), segmentCount - 1)
	const lo = notches[segmentIndex]
	const hi = notches[segmentIndex + 1]
	const step = niceStep(hi - lo)
	return clamp(Math.round(rawValue(percent) / step) * step)
}

function rangeFromPercents(percents: number[], moved: [boolean, boolean]): [number, number] {
	const values = percents.map((percent, index) => (moved[index] ? percentToValue(percent) : props.modelValue[index]))
	return values[0] > values[1] ? [values[1], values[0]] : [values[0], values[1]]
}

const localPercents = ref<number[]>([valueToPercent(props.modelValue[0]), valueToPercent(props.modelValue[1])])
// last raw (unsnapped) percents reported by the slider, used to tell which thumb actually moved
const lastRawPercents = ref<number[]>([...localPercents.value])

const notchPercent = (i: number): number => (i / (notchValues.value.length - 1)) * 100

const clamp = (v: number): number => Math.min(props.max, Math.max(props.min, v))

const displayValue = computed<[number, number]>(() => {
	if (!isDragging.value) return props.modelValue
	return rangeFromPercents(localPercents.value, movedThumbs.value)
})

const isDefaultRange = computed(() => displayValue.value[0] === props.min && displayValue.value[1] === props.max)
const summaryText = computed(() => {
	const [minVal, maxVal] = displayValue.value
	const maxText = maxVal >= props.max ? t("filters.range-max-placeholder") : formatValue(maxVal)
	return `${formatValue(minVal)} – ${maxText}`
})

async function openEdit(): Promise<void> {
	isEditing.value = true
	await nextTick()
	minInputRef.value?.focus()
}

function closeEdit(): void {
	isEditing.value = false
}

function updateInputs(): void {
	if (!isEditingMin.value && minInputRef.value) {
		minInputRef.value.value = props.modelValue[0] === props.min ? "" : formatNumber(props.modelValue[0])
	}
	if (!isEditingMax.value && maxInputRef.value) {
		maxInputRef.value.value = props.modelValue[1] === props.max ? "" : formatNumber(props.modelValue[1])
	}
}

watch(
	() => props.modelValue,
	newVal => {
		if (!newVal) return
		committedValue.value = [...newVal]
		if (!isDragging.value) {
			localPercents.value = [valueToPercent(newVal[0]), valueToPercent(newVal[1])]
			lastRawPercents.value = [...localPercents.value]
		}
		updateInputs()
	},
	{ immediate: true }
)

onMounted(updateInputs)

function emitValue(next: [number, number]): void {
	const from: [number, number] = [...committedValue.value]
	if (from[0] === next[0] && from[1] === next[1]) return

	emit("change", { from, to: next })
	emit("update:modelValue", next)
	committedValue.value = [...next]
}

function previewInputs(percents: number[]): void {
	const [from, to] = rangeFromPercents(percents, movedThumbs.value)
	if (!isEditingMin.value && minInputRef.value) {
		minInputRef.value.value = from === props.min ? "" : formatNumber(from)
	}
	if (!isEditingMax.value && maxInputRef.value) {
		maxInputRef.value.value = to === props.max ? "" : formatNumber(to)
	}
}

function nearestNotchPercent(percent: number): number {
	let closest = 0
	let closestDiff = Infinity
	for (let i = 0; i < notchValues.value.length; i++) {
		const diff = Math.abs(notchPercent(i) - percent)
		if (diff < closestDiff) {
			closestDiff = diff
			closest = notchPercent(i)
		}
	}
	return closest
}

const SNAP_THRESHOLD_PERCENT = 4

function withMagneticSnap(percents: number[]): number[] {
	return percents.map(p => {
		const nearest = nearestNotchPercent(p)
		return Math.abs(nearest - p) <= SNAP_THRESHOLD_PERCENT ? nearest : p
	})
}

// snap the thumb to each step's percent equivalent so dragging feels notched rather than continuous
function stepPercent(percent: number): number {
	return valueToPercent(percentToValue(percent))
}

function handleSliderChange(rawPercents: number[]): void {
	if (!rawPercents?.length) return
	if (!isDragging.value) {
		isDragging.value = true
		dragChangeCount.value = 0
		movedThumbs.value = [false, false]
	}
	dragChangeCount.value++

	// only re-snap the thumb(s) whose raw position actually changed — otherwise the idle thumb
	// can visibly jump when its own (unrelated) raw percent gets re-quantized on every update
	const percents = localPercents.value.slice()
	const snapped = withMagneticSnap(rawPercents)
	rawPercents.forEach((raw, i) => {
		if (i === focusedThumbIndex.value && Math.abs(raw - (lastRawPercents.value[i] ?? raw)) > 1e-9) {
			movedThumbs.value[i] = true
			percents[i] = stepPercent(snapped[i])
		}
	})
	lastRawPercents.value = rawPercents

	localPercents.value = percents
	previewInputs(percents)
}

function handleSliderEnd(): void {
	isDragging.value = false
	if (dragChangeCount.value > 1) suppressNextClick.value = true
	emitValue(rangeFromPercents(localPercents.value, movedThumbs.value))
	movedThumbs.value = [false, false]
}

function handleRailClick(event: MouseEvent): void {
	if (suppressNextClick.value) {
		suppressNextClick.value = false
		return
	}
	const el = event.currentTarget as HTMLElement
	const rect = el.getBoundingClientRect()
	const style = getComputedStyle(el)
	const padLeft = parseFloat(style.paddingLeft) || 0
	const padRight = parseFloat(style.paddingRight) || 0
	const contentLeft = rect.left + padLeft
	const contentWidth = rect.width - padLeft - padRight
	if (contentWidth <= 0) return

	const clickPercent = Math.min(100, Math.max(0, ((event.clientX - contentLeft) / contentWidth) * 100))
	const snapped = nearestNotchPercent(clickPercent)

	const [p0, p1] = localPercents.value
	const closerIndex = Math.abs(p0 - clickPercent) <= Math.abs(p1 - clickPercent) ? 0 : 1
	const nextPercents = closerIndex === 0 ? [snapped, p1] : [p0, snapped]

	localPercents.value = nextPercents
	lastRawPercents.value = [...nextPercents]
	emitValue(rangeFromPercents(nextPercents, [closerIndex === 0, closerIndex === 1]))
}

function handleInput(event: Event, type: "min" | "max"): void {
	const target = event.target as HTMLInputElement
	const raw = target.value.trim()

	if (type === "min") isEditingMin.value = true
	else isEditingMax.value = true

	const value = raw === "" ? (type === "min" ? props.min : props.max) : clamp(parseNumber(raw))
	const next: [number, number] = type === "min" ? [value, props.modelValue[1]] : [props.modelValue[0], value]
	emitValue(next)
}

function handleBlur(type: "min" | "max"): void {
	if (type === "min") isEditingMin.value = false
	else isEditingMax.value = false
	updateInputs()
}

function handleKeydown(event: KeyboardEvent): void {
	if (event.key === "Enter") {
		;(event.target as HTMLInputElement).blur()
	}
	if (event.key === "Escape") closeEdit()
}
</script>

<style scoped>
.range-notched-slider {
	display: grid;
	gap: var(--spacing-md);
	padding-bottom: var(--spacing-md);
	border-bottom: var(--border-width-thin) solid var(--neutral-200);
}

.label {
	font-family: var(--font-family-primary);
	font-size: var(--font-size-small);
	font-weight: var(--font-weight-regular);
	color: var(--neutral-600);
}

.rail-wrapper {
	display: grid;
	gap: var(--spacing-md);
}

.rail {
	width: 100%;
	padding: 0 calc(var(--size-icon-sm) / 2);
}

.rail :deep([data-part="track"]) {
	height: var(--spacing-xs);
	width: 100%;
	border-radius: var(--border-radius-full);
	background: var(--neutral-200);
}

.rail :deep([data-part="range"]) {
	height: 100%;
	border-radius: inherit;
	background: var(--primary-green);
}

.rail :deep([data-part="thumb"]) {
	width: var(--size-icon-sm);
	height: var(--size-icon-sm);
	border-radius: var(--border-radius-full);
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--primary-green);
	box-shadow: var(--shadow-sm);
	top: 50%;
	transform: var(--slider-thumb-transform) translateY(-50%) !important;
}

.rail :deep([data-part="marker"]) {
	top: 50%;
	width: 2px;
	height: 10px;
	border-radius: var(--border-radius-full);
	background: var(--neutral-200);
	transform: translateY(calc(-50% + 2px));
}

.notch-labels {
	height: 1.2em;
	width: 100%;
	padding: 0 calc(var(--size-icon-sm) / 2);
}

.notch-labels-inner {
	position: relative;
	height: 100%;
	width: 100%;
}

.notch-label {
	position: absolute;
	top: 0;
	font-size: var(--font-size-xs);
	color: var(--neutral-600);
	white-space: nowrap;

	&.align-start {
		transform: translateX(0);
	}

	&.align-center {
		transform: translateX(-50%);
	}

	&.align-end {
		transform: translateX(-100%);
	}

	&.active {
		color: var(--primary-green);
		font-weight: var(--font-weight-medium);
	}
}

.inputs,
.summary-row {
	display: flex;
	align-items: center;
	gap: var(--spacing-sm);
	width: 100%;
}

.summary-text {
	font-size: var(--font-size-small);
	font-weight: var(--font-weight-medium);
	color: var(--neutral-900);

	&.placeholder {
		font-weight: var(--font-weight-regular);
		color: var(--neutral-400);
	}
}

.link {
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
	color: var(--primary-green);
	font-size: var(--font-size-small);
	text-decoration: underline;
	text-underline-offset: 2px;

	&:hover {
		color: var(--neutral-900);
	}
}

.close-edit {
	display: flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	width: var(--size-icon-xl);
	height: var(--size-icon-xl);
	padding: 0;
	border: none;
	background: none;
	cursor: pointer;
	font-size: var(--font-size-large);
	line-height: 1;
	color: var(--neutral-600);

	&:hover {
		color: var(--neutral-900);
	}
}

.input-wrapper {
	position: relative;
	display: flex;
	align-items: center;
	flex: 1;
	min-width: 0;
}

.input {
	padding: var(--spacing-xs);
	width: 100%;
	min-width: 0;
	font-size: var(--font-size-small);
	text-align: left;
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-sm);
	background: var(--neutral-0);

	&:focus {
		outline: none;
		border-color: var(--primary-green);
		box-shadow: var(--shadow-sm);
	}

	&:disabled {
		background: var(--neutral-0);
		cursor: not-allowed;
	}

	&:has(+ .unit) {
		padding-right: var(--spacing-lg);
	}
}

.unit {
	position: absolute;
	right: var(--spacing-xs);
	top: 50%;
	transform: translateY(-50%);
	font-size: var(--font-size-small);
	color: var(--neutral-400);
	white-space: nowrap;
	pointer-events: none;
}

.dash {
	color: var(--neutral-400);
}

.sr-only {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}
</style>
